import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { frameDOM } from "../../utils/frame-dom";
import { styleTxn } from "../../utils/style-txn";

type MarqueeDirection = "left" | "right" | "up" | "down";

type MarqueeState = {
  host: HTMLElement;
  track: HTMLDivElement;
  part: HTMLElement;
  parts: HTMLElement[];
  clones: HTMLElement[];
  direction: MarqueeDirection;
  speed: number;
  gap: number;
  fill: boolean;
  pauseOnHover: boolean;
  progressEnabled: boolean;
  progressWasEnabled: boolean;
  hovering: boolean;
  position: number;
  hostSize: number;
  partSize: number;
  cycleWidth: number;
  initialOffset: number;
  lastAppliedTranslate: number;
  partProgressApplied: number[];
  partClass: string;
  partClassApplied: string;
  appliedLayoutKey: string;
  refreshScheduled: boolean;
  refreshQueued: boolean;
  visibleProgressStart: number;
  visibleProgressEnd: number;
};

type MarqueeMeasurement = {
  isVert: boolean;
  hostSize: number;
  partSize: number;
  cycleWidth: number;
  initialOffset: number;
  totalCopies: number;
};

const states = new WeakMap<StringObject, MarqueeState>();
const DEFAULT_SPEED = 60;

const normalizeLoopPosition = (value: number, cycleWidth: number) => {
  if (!(cycleWidth > 0)) return 0;
  if (value >= cycleWidth) return value % cycleWidth;
  if (value < 0) return ((value % cycleWidth) + cycleWidth) % cycleWidth;
  return value;
};

const clampSpeed = (value: number | null) => {
  if (!Number.isFinite(value)) return DEFAULT_SPEED;
  return Math.max(0, Math.abs(value as number));
};

const isVerticalDirection = (dir: MarqueeDirection): boolean => {
  return dir === "up" || dir === "down";
};

export class StringMarquee extends StringModule {
  private onFontsReadyBound: () => void;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "marquee";

    this.cssProperties = [
      { name: "--marquee-progress", syntax: "<number>", initialValue: "0", inherits: false },
    ];

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      {
        key: "marquee-direction",
        type: { type: "enum", values: ["left", "right", "up", "down"] },
        fallback: this.settings["marquee-direction"] ?? "left",
      },
      {
        key: "marquee-speed",
        type: "number",
        fallback: this.settings["marquee-speed"] ?? DEFAULT_SPEED,
      },
      {
        key: "marquee-gap",
        type: "dimension",
        fallback: this.settings["marquee-gap"] ?? 0,
      },
      {
        key: "marquee-fill",
        type: "boolean",
        fallback: this.settings["marquee-fill"] ?? false,
      },
      {
        key: "marquee-pause-on-hover",
        type: "boolean",
        fallback: this.settings["marquee-pause-on-hover"] ?? false,
      },
      {
        key: "marquee-progress",
        type: "boolean",
        fallback: this.settings["marquee-progress"] ?? false,
      },
      {
        key: "marquee-part-class",
        type: "string",
        fallback: this.settings["marquee-part-class"] ?? "",
      },
    ];

    this.onFontsReadyBound = this.onFontsReady.bind(this);
  }

  override onInit(): void {
    if ("fonts" in document && document.fonts) {
      document.fonts.ready.then(this.onFontsReadyBound);
      document.fonts.addEventListener?.("loadingdone", this.onFontsReadyBound);
    }
  }

  override onUnsubscribe(): void {
    if ("fonts" in document && document.fonts) {
      document.fonts.removeEventListener?.("loadingdone", this.onFontsReadyBound);
    }
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);

    let state = states.get(object);
    if (!state) {
      state = this.createState(object.htmlElement);
      states.set(object, state);
      this.mountStructure(state);
    }

    this.syncConfig(object, state);
    this.refresh(state);
  }

  override onObjectDisconnected(object: StringObject): void {
    const state = states.get(object);
    if (!state) return;

    state.host.removeEventListener("mouseenter", this.onMouseEnter);
    state.host.removeEventListener("mouseleave", this.onMouseLeave);
  }

  override onResizeWidth(): void {
    this.refreshAll();
  }

  private refreshAll(): void {
    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (!state) continue;
      this.syncConfig(this.objectsOnPage[i], state);
      this.refresh(state);
    }
  }

  override onFrame(data: StringData): void {
    const dt = Math.max(1 / 240, Math.min(0.1, (data.time.delta || 16.6667) / 1000));

    styleTxn.run(() => {
      for (let i = 0; i < this.objectsOnPage.length; i++) {
        const state = states.get(this.objectsOnPage[i]);
        if (!state || !(state.cycleWidth > 0)) continue;
        if (!(state.pauseOnHover && state.hovering) && state.speed > 0) {
          state.position = normalizeLoopPosition(
            state.position + state.speed * dt,
            state.cycleWidth,
          );
        }

        const isInv = state.direction === "right" || state.direction === "down";
        const translate =
          (isInv ? state.position - state.cycleWidth : -state.position) - state.initialOffset;

        if (translate === state.lastAppliedTranslate) continue;
        state.lastAppliedTranslate = translate;

        const transformStr = isVerticalDirection(state.direction)
          ? `translate3d(0, ${translate}px, 0)`
          : `translate3d(${translate}px, 0, 0)`;

        styleTxn.setProp(state.track, "transform", transformStr);
        this.updatePartProgress(state, translate);
      }
    });
  }

  private createState(host: HTMLElement): MarqueeState {
    const track = document.createElement("div");
    const part = document.createElement("span");

    track.setAttribute("data-string-marquee-track", "");
    part.setAttribute("data-string-marquee-part", "");

    host.addEventListener("mouseenter", this.onMouseEnter);
    host.addEventListener("mouseleave", this.onMouseLeave);

    return {
      host,
      track,
      part,
      parts: [part],
      clones: [],
      direction: "left",
      speed: DEFAULT_SPEED,
      gap: 0,
      fill: false,
      pauseOnHover: false,
      progressEnabled: false,
      progressWasEnabled: false,
      hovering: false,
      position: 0,
      hostSize: 0,
      partSize: 0,
      cycleWidth: 0,
      initialOffset: 0,
      lastAppliedTranslate: Number.NaN,
      partProgressApplied: [Number.NaN],
      partClass: "",
      partClassApplied: "",
      appliedLayoutKey: "",
      refreshScheduled: false,
      refreshQueued: false,
      visibleProgressStart: -1,
      visibleProgressEnd: -1,
    };
  }

  private mountStructure(state: MarqueeState): void {
    const { host, track, part } = state;

    while (host.firstChild) {
      part.appendChild(host.firstChild);
    }

    track.appendChild(part);
    host.appendChild(track);

    styleTxn.run(() => {
      styleTxn.setProps(host, {
        overflow: "hidden",
      });
      styleTxn.setProps(track, {
        display: "flex",
        willChange: "transform",
      });
      styleTxn.setProps(part, {
        flex: "0 0 auto",
      });
    });
  }

  private syncConfig(object: StringObject, state: MarqueeState): void {
    state.direction = object.getProperty<MarqueeDirection>("marquee-direction") ?? "left";
    state.speed = clampSpeed(object.getProperty<number>("marquee-speed"));
    state.gap = Math.max(0, object.getProperty<number>("marquee-gap") ?? 0);
    state.fill = object.getProperty<boolean>("marquee-fill") === true;
    state.pauseOnHover = object.getProperty<boolean>("marquee-pause-on-hover") === true;
    state.progressEnabled = object.getProperty<boolean>("marquee-progress") === true;
    state.partClass = object.getProperty<string>("marquee-part-class") ?? "";
  }

  private refresh(state: MarqueeState): void {
    this.applyStaticLayout(state);
    this.scheduleRefresh(state);
  }

  private applyStaticLayout(state: MarqueeState): void {
    this.syncPartClass(state);
    const isVert = isVerticalDirection(state.direction);
    const layoutKey = `${state.direction}|${state.gap}`;
    if (state.appliedLayoutKey === layoutKey) return;

    state.appliedLayoutKey = layoutKey;

    styleTxn.run(() => {
      styleTxn.setProps(state.track, {
        flexDirection: isVert ? "column" : "row",
        flexWrap: "nowrap",
        width: isVert ? "100%" : "max-content",
        height: isVert ? "max-content" : "100%",
        rowGap: isVert ? `${state.gap}px` : "0px",
        columnGap: isVert ? "0px" : `${state.gap}px`,
      });
    });
  }

  private measure(state: MarqueeState): MarqueeMeasurement {
    const isVert = isVerticalDirection(state.direction);
    const hostRect = state.host.getBoundingClientRect();
    const partRect = state.part.getBoundingClientRect();
    const computedHost = getComputedStyle(state.host);

    const hostSize = isVert ? hostRect.height : hostRect.width;
    const partSize = isVert ? partRect.height : partRect.width;
    const cycleWidth = partSize > 0 ? partSize + state.gap : 0;

    let totalCopies = 1;
    if (hostSize > 0 && cycleWidth > 0) {
      const minCopies = Math.max(2, Math.ceil(hostSize / cycleWidth) + 2);
      totalCopies = state.fill ? minCopies : isVert ? minCopies : 2;
    }

    return {
      isVert,
      hostSize,
      partSize,
      cycleWidth,
      initialOffset: isVert
        ? parseFloat(computedHost.paddingTop)
        : parseFloat(computedHost.paddingLeft),
      totalCopies,
    };
  }

  private applyMeasurement(state: MarqueeState, measurement: MarqueeMeasurement): void {
    this.syncCopies(state, measurement.totalCopies);

    state.hostSize = measurement.hostSize;
    state.partSize = measurement.partSize;
    state.cycleWidth = measurement.cycleWidth;
    state.initialOffset = measurement.initialOffset;

    state.position = normalizeLoopPosition(state.position, state.cycleWidth);
    state.lastAppliedTranslate = Number.NaN;

    for (let i = 0; i < state.partProgressApplied.length; i++) {
      state.partProgressApplied[i] = Number.NaN;
    }
    state.visibleProgressStart = -1;
    state.visibleProgressEnd = -1;

    styleTxn.run(() => {
      const isInv = state.direction === "right" || state.direction === "down";
      const resetVal =
        (isInv ? state.position - state.cycleWidth : -state.position) - state.initialOffset;
      const resetTransform = measurement.isVert
        ? `translate3d(0, ${resetVal}px, 0)`
        : `translate3d(${resetVal}px, 0, 0)`;
      styleTxn.setProp(state.track, "transform", resetTransform);
      if (!state.progressEnabled && state.progressWasEnabled) {
        for (let i = 0; i < state.parts.length; i++) {
          styleTxn.setVar(state.parts[i], "--marquee-progress", 0);
        }
      }
    });

    state.progressWasEnabled = state.progressEnabled;
  }

  private scheduleRefresh(state: MarqueeState): void {
    if (state.refreshScheduled) {
      state.refreshQueued = true;
      return;
    }

    state.refreshScheduled = true;

    frameDOM.measure(() => {
      const measurement = this.measure(state);
      frameDOM.mutate(() => {
        state.refreshScheduled = false;
        this.applyMeasurement(state, measurement);

        if (state.refreshQueued) {
          state.refreshQueued = false;
          this.scheduleRefresh(state);
        }
      });
    });
  }

  private syncCopies(state: MarqueeState, totalCopies: number): void {
    if (state.parts.length === totalCopies) {
      if (state.partProgressApplied.length !== state.parts.length) {
        state.partProgressApplied = new Array(state.parts.length).fill(Number.NaN);
      }
      return;
    }

    for (let i = 0; i < state.clones.length; i++) {
      state.clones[i].remove();
    }
    state.clones.length = 0;
    const fragment = document.createDocumentFragment();

    for (let i = 1; i < totalCopies; i++) {
      const clone = state.part.cloneNode(true) as HTMLElement;
      clone.removeAttribute("data-string-marquee-part");
      clone.setAttribute("aria-hidden", "true");
      fragment.appendChild(clone);
      state.clones.push(clone);
    }

    state.track.appendChild(fragment);
    state.parts = [state.part, ...state.clones];
    state.partProgressApplied = new Array(state.parts.length).fill(Number.NaN);
  }

  private syncPartClass(state: MarqueeState): void {
    if (state.partClassApplied === state.partClass) return;
    state.partClassApplied = state.partClass;
    state.part.className = state.partClass;
  }

  private updatePartProgress(state: MarqueeState, translate: number): void {
    if (!state.progressEnabled || !(state.hostSize > 0) || !(state.partSize > 0)) {
      return;
    }

    const denominator = state.hostSize + state.partSize;
    if (!(denominator > 0)) return;

    const isInv = state.direction === "right" || state.direction === "down";
    const visibleStart = -state.partSize;
    const visibleEnd = state.hostSize;
    const minCoordinate = visibleStart - state.partSize;
    const maxCoordinate = visibleEnd;
    const maxIndex = state.parts.length - 1;
    const rangeStart = Math.max(
      0,
      Math.floor((minCoordinate - translate) / state.cycleWidth) + 1,
    );
    const rangeEnd = Math.min(
      maxIndex,
      Math.ceil((maxCoordinate - translate) / state.cycleWidth) - 1,
    );

    if (state.visibleProgressStart !== -1 && state.visibleProgressEnd !== -1) {
      for (let i = state.visibleProgressStart; i < rangeStart && i <= state.visibleProgressEnd; i++) {
        state.partProgressApplied[i] = Number.NaN;
      }
      for (let i = Math.max(rangeEnd + 1, state.visibleProgressStart); i <= state.visibleProgressEnd; i++) {
        state.partProgressApplied[i] = Number.NaN;
      }
    }

    state.visibleProgressStart = rangeStart <= rangeEnd ? rangeStart : -1;
    state.visibleProgressEnd = rangeStart <= rangeEnd ? rangeEnd : -1;

    if (rangeStart > rangeEnd) {
      return;
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      const coordinate = translate + i * state.cycleWidth;
      const partStart = coordinate;
      const partEnd = coordinate + state.partSize;
      const visible = partEnd > visibleStart && partStart < visibleEnd;

      if (!visible) {
        if (!Number.isNaN(state.partProgressApplied[i])) {
          state.partProgressApplied[i] = Number.NaN;
        }
        continue;
      }

      const raw = (coordinate + state.partSize) / denominator;
      const clamped = raw <= 0 ? 0 : raw >= 1 ? 1 : raw;
      const progress = isInv ? clamped : 1 - clamped;

      if (state.partProgressApplied[i] === progress) {
        continue;
      }

      state.partProgressApplied[i] = progress;
      styleTxn.setVar(state.parts[i], "--marquee-progress", progress);
    }
  }

  private onFontsReady(): void {
    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (!state) continue;
      this.refresh(state);
    }
  }

  private onMouseEnter = (event: Event) => {
    const host = event.currentTarget as HTMLElement | null;
    if (!host) return;

    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (state?.host === host) {
        state.hovering = true;
        return;
      }
    }
  };

  private onMouseLeave = (event: Event) => {
    const host = event.currentTarget as HTMLElement | null;
    if (!host) return;

    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const state = states.get(this.objectsOnPage[i]);
      if (state?.host === host) {
        state.hovering = false;
        return;
      }
    }
  };
}
